import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL } from "../../config/constants";
import AppSidebar from "../../components/AppSidebar";
import {
  Layout, Main, Content,
  TitleRow, PageTitle, TabsRow, Tab,
  ActivityPanel, PanelHeader, TypeIconWrap, TypeInfo, TypeName, TypeSubtitle, SelectedCounter,
  ActivitiesGrid, ActivityCard, CardTop, Checkbox, CardName, EditIconBtn,
  TagsRow, Tag, CardDescription, LoadingMsg,
} from "./styles";

/* ── Icons ── */
const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TypeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v6"/>
    <path d="M9 10l3 2 3-2"/>
    <path d="M9 17l3 2 3-2"/>
  </svg>
);

const ACTIVITY_TAGS = {
  1:  ["Fine M.S.", "Pincer grasp"],        2:  ["Fine M.S.", "In-hand manipulation"],
  3:  ["Fine M.S.", "In-hand manipulation"], 4:  ["Fine M.S.", "Finger isolation"],
  5:  ["Fine M.S.", "Finger isolation"],     6:  ["Gross M.S.", "Postural control"],
  7:  ["Gross M.S.", "Postural control"],    8:  ["Gross M.S.", "Balance"],
  9:  ["Vision", "Fixation"],                10: ["Vision", "Smooth pursuit"],
  11: ["Vision", "Smooth pursuit"],          12: ["Vision", "Auditory"],
  13: ["Vision", "Vestibular"],              14: ["Vision", "Gustatory"],
  15: ["Vision", "Vestibular"],              16: ["Vision", "Auditory"],
  17: ["Memory", "Short-term"],              18: ["Attention", "Focus"],
  19: ["Language", "Comprehension"],         20: ["Problem solving"],
  21: ["Executive function"],                22: ["Planning", "Sequencing"],
  23: ["Working memory"],                    24: ["Cognitive flexibility"],
  25: ["Tactile", "Texture"],                26: ["Proprioception"],
  27: ["Vestibular", "Balance"],             28: ["Auditory", "Discrimination"],
  29: ["Visual", "Tracking"],                30: ["Olfactory", "Gustatory"],
  31: ["Interoception"],                     32: ["Multi-sensory"],
  33: ["Social", "Turn-taking"],             34: ["Communication", "Verbal"],
  35: ["Imitation", "Gesture"],              36: ["Joint attention"],
  37: ["Pragmatic language"],                38: ["Emotional regulation"],
  39: ["Peer interaction"],                  40: ["Non-verbal communication"],
};

const LOREM = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.";

/* ── Component ── */
const TherapistActivityPlan = () => {
  const { id }     = useParams();
  const location   = useLocation();
  const { t }      = useTranslation();
  const token      = localStorage.getItem("token");

  const [patient]                       = useState(location.state?.patient || null);
  const [activeTypeId, setActiveTypeId] = useState(1);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activities, setActivities]     = useState([]);
  const [prescribed, setPrescribed]     = useState(new Set());
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [typesRes, actRes, presRes] = await Promise.all([
          fetch(`${API_URL}/activities/types`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/activities`,        { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/patients/${id}/prescriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const typesData = await typesRes.json();
        const actData   = await actRes.json();
        const presData  = await presRes.json();
        setActivityTypes(Array.isArray(typesData) ? typesData : []);
        setActivities(Array.isArray(actData)   ? actData   : []);
        setPrescribed(new Set(Array.isArray(presData) ? presData : []));
      } catch (err) {
        console.error("TherapistActivityPlan fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]); // eslint-disable-line

  const handleToggle = async (activityId, e) => {
    e.stopPropagation();
    const next = new Set(prescribed);
    if (next.has(activityId)) next.delete(activityId);
    else next.add(activityId);
    setPrescribed(next);

    try {
      await fetch(`${API_URL}/patients/${id}/prescriptions/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activity_id: activityId }),
      });
    } catch (err) {
      setPrescribed(prescribed);
      console.error("toggle error:", err);
    }
  };

  const typeActivities = activities.filter((a) => a.activity_type_id === activeTypeId);
  const selectedCount  = typeActivities.filter((a) => prescribed.has(a.id)).length;
  const activeType     = activityTypes.find((type) => type.id === activeTypeId) || {};

  return (
    <Layout>
      <AppSidebar patient={patient} activeNav="activityPlan" />

      <Main>
        <Content>
          <TitleRow>
            <PageTitle>Activity Plan</PageTitle>
            <TabsRow>
              {activityTypes.map((type) => (
                <Tab key={type.id} $active={type.id === activeTypeId} onClick={() => setActiveTypeId(type.id)}>
                  {t(`activityTypes.${type.id}.name`, { defaultValue: type.name })}
                </Tab>
              ))}
            </TabsRow>
          </TitleRow>

          <ActivityPanel>
            <PanelHeader>
              <TypeIconWrap><TypeIcon /></TypeIconWrap>
              <TypeInfo>
                <TypeName>{t(`activityTypes.${activeType.id}.name`, { defaultValue: activeType.name })}</TypeName>
                <TypeSubtitle>{t(`activityTypes.${activeType.id}.description`, { defaultValue: activeType.description })}</TypeSubtitle>
              </TypeInfo>
              <SelectedCounter>{selectedCount}/{typeActivities.length} SELECTED</SelectedCounter>
            </PanelHeader>



            {loading ? (
              <LoadingMsg>Loading activities…</LoadingMsg>
            ) : (
              <ActivitiesGrid>
                {typeActivities.map((act) => {
                  const isSelected = prescribed.has(act.id);
                  const tags       = ACTIVITY_TAGS[act.id] || [];
                  return (
                    <ActivityCard key={act.id} $selected={isSelected} onClick={(e) => handleToggle(act.id, e)}>
                      <CardTop>
                        <Checkbox $checked={isSelected}>
                          {isSelected && <CheckIcon />}
                        </Checkbox>
                        <CardName>{act.name}</CardName>
                        <EditIconBtn onClick={(e) => e.stopPropagation()}>
                          <PencilIcon />
                        </EditIconBtn>
                      </CardTop>
                      <TagsRow>
                        {tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                      </TagsRow>
                      <CardDescription>{LOREM}</CardDescription>
                    </ActivityCard>
                  );
                })}
              </ActivitiesGrid>
            )}
          </ActivityPanel>
        </Content>
      </Main>
    </Layout>
  );
};

export default TherapistActivityPlan;
